/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/ConfirmationModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import styles from "./index.module.css";

const ConfirmationModal = ({
  dialogRef = null,
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText,
  cancelButtonText,
  isLoading = false,
  loadingButtonText,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (dialogRef && dialogRef.current) {
      const dialog = dialogRef.current;
      if (open) {
        dialog.showModal();
      } else {
        dialog.close();
      }
    }
  }, [open, dialogRef]);

  useEffect(() => {
    // Cleanup: close dialog when component unmounts
    return () => {
      if (dialogRef && dialogRef.current) {
        dialogRef.current.close();
      }
    };
  }, [dialogRef]);

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // if (!open) return null;

  return (
    <>
      {open && <div className={styles.modalOverlay}></div>}
      <dialog ref={dialogRef} className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>
            {title || t("components.confirmationModal.title")}
          </h3>
          <button
            className={styles.closeButton}
            onClick={handleCancel}
            disabled={isLoading}
          >
            &times;
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.modalMessage}>
            {message || t("components.confirmationModal.message")}
          </p>
        </div>

        <div className={styles.modalActions}>
          <button
            className={`${styles.modalButton} ${styles.cancelButton}`}
            onClick={handleCancel}
            disabled={isLoading}
          >
            {cancelButtonText || t("components.confirmationModal.cancel")}
          </button>
          <button
            className={`${styles.modalButton} ${styles.confirmButton}`}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className={styles.loadingText}>
                {loadingButtonText ||
                  t("components.confirmationModal.deleting")}
              </span>
            ) : (
              confirmButtonText || t("components.confirmationModal.confirm")
            )}
          </button>
        </div>
      </dialog>
    </>
  );
};

export default ConfirmationModal;
